import { useMutation, useQuery } from '@apollo/client'
import { Edit } from '@mui/icons-material'
import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Typography,
} from '@mui/material'
import ToggleEditAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditAutocomplete'
import ToggleEditDatePicker from 'candidate/components/ToggleEditFormFields/ToggleEditDatePicker'
import ToggleEditMultiSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditMultiSelectField'
import ToggleEditTextField from 'candidate/components/ToggleEditFormFields/ToggleEditTextField'
import {
    CandidateAboutMeOptionsQuery,
    UpdateCandidateAboutMeMutation,
} from 'candidate/graphql/profile.gql'
import { AboutMeFormValues } from 'candidate/models/profile'
import { formatDate } from 'date-fns'
import React, {
    FC,
    FormEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useSelector } from 'react-redux'
import useToast from 'shared/components/Toast/useToast'
import { selectUser } from 'shared/state/userSlice'
import { getPxAsRem } from 'shared/utils'
import {
    EditButtonContainer,
    StyledForm,
    TitleContainer,
} from '../../components/styles'
import { FormComponentProps, PROFILE_SECTIONS } from './types'

const AboutMe: FC<FormComponentProps> = ({
    loading = false,
    data,
    refetch,
    edit,
    setEdit,
    setUnsavedChangesExist,
    closeEditWithoutModal,
}) => {
    const toast = useToast()
    const user = useSelector(selectUser)
    const aboutMeOptions = useQuery(CandidateAboutMeOptionsQuery)
    const { id } = user
    const employmentType = data.candidate?.employmentType
    const startYear = data.candidate?.startYear
    const city = data.candidate?.city
    const province = data.candidate?.province
    const state = data.candidate?.state
    const country = data.candidate?.country
    const gender = data.candidate?.gender?.gender
    const lgbtq = data.candidate?.lgbtq?.lgbtq
    const visaStatuses = data.candidate?.visaStatuses
    const ethnicities = data.candidate?.ethnicities
    const programs = data.candidate?.programs
    const veteranStatus = data.candidate?.veteranStatus?.veteranStatus
    const languages = data.candidate?.languages

    const initialFormValues: AboutMeFormValues = useMemo(
        () => ({
            employmentType: {
                id: employmentType?.id ?? '',
                value: employmentType?.value ?? '',
            },
            startYear: startYear ? new Date(`${startYear}T00:00`) : undefined,
            city: city ?? '',
            province: province
                ? { id: province.id, value: province.value }
                : undefined,
            state: state ? { id: state.id, value: state.value } : undefined,
            country: country
                ? { id: country.id, value: country.value }
                : undefined,
            gender: { id: gender?.id ?? '', value: gender?.value ?? '' },
            lgbtq: { id: lgbtq?.id ?? '', value: lgbtq?.value ?? '' },
            visaStatusIds:
                visaStatuses?.map((val) => val.visaStatus?.id ?? 'unknown') ??
                [],
            ethnicityIds:
                ethnicities?.map((val) => val.ethnicity?.id ?? 'unknown') ?? [],
            programIds: programs?.map((val) => val.id) ?? [],
            veteranStatus: {
                id: veteranStatus?.id ?? '',
                value: veteranStatus?.value ?? '',
            },
            languageIds:
                languages?.map((val) => val.language?.id ?? 'unknown') ?? [],
        }),
        [
            employmentType,
            startYear,
            city,
            province,
            state,
            country,
            gender,
            lgbtq,
            visaStatuses,
            ethnicities,
            programs,
            veteranStatus,
            languages,
        ]
    )

    const [formValues, setFormValues] =
        useState<AboutMeFormValues>(initialFormValues)

    const [mutate, { loading: isSubmitting }] = useMutation(
        UpdateCandidateAboutMeMutation,
        {
            onCompleted(data) {
                closeEditWithoutModal()
                refetch()
                toast({
                    title: 'Success',
                    message: 'Your profile has been updated',
                    severity: 'success',
                })
            },
            onError(error) {
                console.error('about me update error: ', error)
                // TODO: more detailed error message?
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            },
        }
    )

    // form handlers
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        mutate({
            variables: {
                id: id,
                employmentTypeId: formValues.employmentType?.id,
                startYear: formValues.startYear
                    ? formatDate(formValues.startYear, 'yyyy')
                    : '',
                city: formValues.city,
                provinceId: formValues.province?.id,
                stateId: formValues.state?.id,
                countryId: formValues.country?.id,
                genderId: formValues.gender?.id,
                lgbtqId: formValues.lgbtq?.id,
                visaStatusIds: formValues.visaStatusIds,
                ethnicityIds: formValues.ethnicityIds,
                programIds: formValues.programIds,
                veteranStatusId: formValues.veteranStatus?.id,
                languageIds: formValues.languageIds,
            },
        })
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }))
    }

    const handleEditClick = () => {
        // leaving edit mode
        if (edit) {
            setEdit(null)
        } else {
            setEdit(PROFILE_SECTIONS.ABOUT_ME)
        }
    }

    const resetForm = useCallback(() => {
        setFormValues(initialFormValues)
    }, [setFormValues, initialFormValues])

    useEffect(() => {
        if (!edit) resetForm()
    }, [edit, resetForm])

    // memoize selected options
    const selectedCountryOption = useMemo(
        () =>
            aboutMeOptions.data?.countries.find(
                (option) => option.id === formValues.country?.id
            ),
        [formValues.country, aboutMeOptions.data?.countries]
    )
    const selectedEmploymentOption = useMemo(
        () =>
            aboutMeOptions.data?.employmentTypes.find(
                (option) => option.id === formValues.employmentType?.id
            ),
        [formValues.employmentType, aboutMeOptions.data?.employmentTypes]
    )
    const selectedGenderOption = useMemo(
        () =>
            aboutMeOptions.data?.genders.find(
                (gender) => gender.id === formValues.gender?.id
            ),
        [aboutMeOptions.data?.genders, formValues.gender]
    )
    const selectedProgramOption = useMemo(
        () =>
            aboutMeOptions.data?.programs.filter((program) =>
                formValues.programIds?.includes(program.id)
            ),
        [aboutMeOptions.data?.programs, formValues.programIds]
    )
    const selectedLgbtqOption = useMemo(
        () =>
            aboutMeOptions.data?.lgbtq.find(
                (lgbtq) => lgbtq.id === formValues.lgbtq?.id
            ),
        [aboutMeOptions.data?.lgbtq, formValues.lgbtq]
    )
    const selectedVeteranStatusOption = useMemo(
        () =>
            aboutMeOptions.data?.veteranStatuses.find(
                (veteranStatus) =>
                    veteranStatus.id === formValues.veteranStatus?.id
            ),
        [aboutMeOptions.data?.veteranStatuses, formValues.veteranStatus]
    )
    const selectedLanguages = useMemo(
        () =>
            aboutMeOptions.data?.languages.filter((language) =>
                formValues.languageIds?.includes(language.id)
            ),
        [aboutMeOptions.data?.languages, formValues.languageIds]
    )
    const selectedEthnicities = useMemo(
        () =>
            aboutMeOptions.data?.ethnicities.filter((ethnicity) =>
                formValues.ethnicityIds?.includes(ethnicity.id)
            ),
        [aboutMeOptions.data?.ethnicities, formValues.ethnicityIds]
    )
    const selectedVisaStatuses = useMemo(
        () =>
            aboutMeOptions.data?.visaStatuses.filter((visaStatus) =>
                formValues.visaStatusIds?.includes(visaStatus.id)
            ),
        [aboutMeOptions.data?.visaStatuses, formValues.visaStatusIds]
    )

    const haveDatesChanged = useMemo(() => {
        if (formValues.startYear) {
            if (startYear) {
                return formatDate(formValues.startYear, 'yyyy') !== startYear
            } else {
                return true
            }
        } else {
            if (!startYear) return false
            return true
        }
    }, [formValues.startYear, startYear])

    const unsavedChangesExist = useMemo(
        () =>
            formValues.city !== city ||
            formValues.country?.id !== country?.id ||
            formValues.state?.id !== state?.id ||
            formValues.province?.id !== province?.id ||
            formValues.employmentType?.id !== employmentType?.id ||
            haveDatesChanged ||
            formValues.gender?.id !== gender?.id ||
            formValues.lgbtq?.id !== lgbtq?.id ||
            formValues.veteranStatus?.id !== veteranStatus?.id ||
            JSON.stringify(formValues.visaStatusIds) !==
                JSON.stringify(
                    visaStatuses?.map((val) => val.visaStatus?.id)
                ) ||
            JSON.stringify(formValues.programIds) !==
                JSON.stringify(programs?.map((val) => val.id)) ||
            JSON.stringify(formValues.ethnicityIds) !==
                JSON.stringify(ethnicities?.map((val) => val.ethnicity?.id)) ||
            JSON.stringify(formValues.languageIds) !==
                JSON.stringify(languages?.map((val) => val.language?.id)),

        [
            formValues.city,
            formValues.country?.id,
            formValues.state?.id,
            formValues.province?.id,
            formValues.employmentType?.id,
            formValues.gender?.id,
            formValues.lgbtq?.id,
            formValues.veteranStatus?.id,
            formValues.visaStatusIds,
            formValues.programIds,
            formValues.ethnicityIds,
            formValues.languageIds,
            city,
            country?.id,
            state?.id,
            province?.id,
            employmentType?.id,
            haveDatesChanged,
            gender?.id,
            lgbtq?.id,
            veteranStatus?.id,
            visaStatuses,
            programs,
            ethnicities,
            languages,
        ]
    )

    useEffect(
        () => setUnsavedChangesExist(unsavedChangesExist),
        [unsavedChangesExist, setUnsavedChangesExist]
    )

    const getStateOrProvinceField = () => {
        const usa = aboutMeOptions.data?.countries.find(
            (country) => country.value === 'United States'
        )
        const canada = aboutMeOptions.data?.countries.find(
            (country) => country.value === 'Canada'
        )
        if (formValues.country?.id === usa?.id) {
            const selectedState = aboutMeOptions.data?.states.find(
                (option) => option.id === formValues.state?.id
            )
            return (
                <ToggleEditAutocomplete
                    label="States"
                    labelId="states"
                    edit={edit}
                    options={aboutMeOptions.data?.states || []}
                    value={selectedState || { id: '', value: '' }}
                    onChange={(event, newValue) => {
                        if (newValue && !Array.isArray(newValue)) {
                            setFormValues((prevState) => ({
                                ...prevState,
                                state: newValue,
                                province: undefined,
                            }))
                        }
                    }}
                />
            )
        } else if (formValues.country?.id === canada?.id) {
            const selectedProvince = aboutMeOptions.data?.provinces.find(
                (option) => option.id === formValues.province?.id
            )
            return (
                <ToggleEditAutocomplete
                    label="Provinces"
                    labelId="provinces"
                    edit={edit}
                    options={aboutMeOptions.data?.provinces || []}
                    value={selectedProvince || { id: '', value: '' }}
                    onChange={(event, newValue) => {
                        if (newValue && !Array.isArray(newValue)) {
                            setFormValues((prevState) => ({
                                ...prevState,
                                province: newValue,
                                state: undefined,
                            }))
                        }
                    }}
                />
            )
        } else {
            return (
                <ToggleEditAutocomplete
                    disabled
                    label="State/Province"
                    labelId=""
                    edit={edit}
                    options={[]}
                />
            )
        }
    }

    return (
        <>
            <TitleContainer>
                <Typography variant="h6" component="h2">
                    About Me
                </Typography>
                <IconButton onClick={handleEditClick} disabled={loading}>
                    <Edit fontSize="small" />
                </IconButton>
            </TitleContainer>
            <StyledForm onSubmit={handleSubmit}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Current Employment Type"
                            options={aboutMeOptions.data?.employmentTypes || []}
                            onChange={(value, newValue) => {
                                if (newValue && !Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        employmentType: newValue,
                                    }))
                                }
                            }}
                            labelId="employmentType"
                            value={
                                selectedEmploymentOption || {
                                    id: '',
                                    value: '',
                                }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditDatePicker
                            edit={edit}
                            value={formValues.startYear}
                            views={['year']}
                            format="yyyy"
                            onChange={(value: Date | null) => {
                                setFormValues((prevState) => ({
                                    ...prevState,
                                    startYear: value || undefined,
                                }))
                            }}
                            label="Start Year"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Country"
                            options={aboutMeOptions.data?.countries || []}
                            onChange={(event, newValue) => {
                                if (newValue && !Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        country: newValue,
                                        stateId:
                                            newValue?.value !== 'United States'
                                                ? undefined
                                                : prevState.state,
                                        provinceId:
                                            newValue?.value !== 'Canada'
                                                ? undefined
                                                : prevState.province,
                                    }))
                                }
                            }}
                            labelId="country"
                            value={
                                selectedCountryOption || { id: '', value: '' }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {getStateOrProvinceField()}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditTextField
                            name="city"
                            value={formValues.city || ''}
                            onChange={handleChange}
                            onBlur={() => {}}
                            id="city"
                            edit={edit}
                            label="City"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Gender"
                            options={aboutMeOptions.data?.genders || []}
                            onChange={(event, newValue) => {
                                if (newValue && !Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        gender: newValue,
                                    }))
                                }
                            }}
                            labelId="gender"
                            value={
                                selectedGenderOption || { id: '', value: '' }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditMultiSelectField
                            limit={10}
                            edit={edit}
                            label="Race or Ethnicity"
                            dropdownData={
                                aboutMeOptions.data?.ethnicities || []
                            }
                            onInputChange={(newValue) => {
                                setFormValues((prevState) => ({
                                    ...prevState,
                                    ethnicityIds: newValue.map(
                                        (value) => value.id
                                    ),
                                }))
                            }}
                            labelId="ethnicity"
                            InputProps={
                                selectedEthnicities?.length
                                    ? undefined
                                    : {
                                          sx: {
                                              padding: `${getPxAsRem(14, 0, 14, 6)} !important`,
                                          },
                                      }
                            }
                            selectedDropdownData={selectedEthnicities || []}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditMultiSelectField
                            limit={10}
                            edit={edit}
                            label="Programs"
                            dropdownData={aboutMeOptions.data?.programs || []}
                            onInputChange={(newValue) => {
                                setFormValues((prevState) => ({
                                    ...prevState,
                                    programIds: newValue.map(
                                        (value) => value.id
                                    ),
                                }))
                            }}
                            InputProps={
                                selectedProgramOption?.length
                                    ? undefined
                                    : {
                                          sx: {
                                              padding: `${getPxAsRem(14, 0, 14, 6)} !important`,
                                          },
                                      }
                            }
                            labelId="programs"
                            selectedDropdownData={selectedProgramOption}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Member of the LGBTQ Community"
                            options={aboutMeOptions.data?.lgbtq || []}
                            onChange={(event, newValue) => {
                                if (newValue && !Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        lgbtq: newValue,
                                    }))
                                }
                            }}
                            labelId="lgbtq"
                            value={selectedLgbtqOption || { id: '', value: '' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Veteran Status"
                            options={aboutMeOptions.data?.veteranStatuses || []}
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        veteranStatusId: newValue,
                                    }))
                                }
                            }}
                            labelId="veteranStatus"
                            value={
                                selectedVeteranStatusOption || {
                                    id: '',
                                    value: '',
                                }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditMultiSelectField
                            edit={edit}
                            limit={10}
                            label="Current Visa Status"
                            dropdownData={
                                aboutMeOptions.data?.visaStatuses || []
                            }
                            onInputChange={(newValue) => {
                                setFormValues((prevState) => ({
                                    ...prevState,
                                    visaStatusIds: newValue.map(
                                        (value) => value.id
                                    ),
                                }))
                            }}
                            labelId="visaStatuses"
                            selectedDropdownData={selectedVisaStatuses}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditMultiSelectField
                            limit={10}
                            edit={edit}
                            label="Other Languages"
                            dropdownData={aboutMeOptions.data?.languages || []}
                            onInputChange={(newValue) => {
                                setFormValues((prevState) => ({
                                    ...prevState,
                                    languageIds: newValue.map(
                                        (value) => value.id
                                    ),
                                }))
                            }}
                            InputProps={
                                selectedLanguages?.length
                                    ? undefined
                                    : {
                                          sx: {
                                              padding: `${getPxAsRem(14, 0, 14, 6)} !important`,
                                          },
                                      }
                            }
                            labelId="otherLanguages"
                            selectedDropdownData={selectedLanguages}
                        />
                    </Grid>
                    {edit && (
                        <EditButtonContainer item xs={12}>
                            <Button
                                variant="outlined"
                                type="reset"
                                onClick={() => {
                                    resetForm()
                                    closeEditWithoutModal()
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting || !unsavedChangesExist}
                                endIcon={
                                    isSubmitting ? (
                                        <CircularProgress size={14} />
                                    ) : undefined
                                }
                            >
                                Save and Update
                            </Button>
                        </EditButtonContainer>
                    )}
                </Grid>
            </StyledForm>
        </>
    )
}

export default AboutMe
